<template>
  <div class="mic-check-panel">
    <p class="guide-txt">お子さまの声を録音するための設定です。</p>
    <p
      v-if="micCheckRunning"
      class="guide-txt mic-check-running-hint"
      role="status"
      aria-live="polite"
    >
      <strong>Chrome のアドレスバー付近</strong>（鍵・マイクのアイコンなど）に、マイクの許可やブロックを選ぶ表示が出ていないかご確認ください。別ウィンドウの裏や、つなげている別のモニター側に出ていることもあります。
    </p>

    <button
      type="button"
      class="btn-action"
      @click="runMicCheck"
      :disabled="micCheckRunning"
    >
      {{ micCheckRunning ? 'テスト中...' : 'マイクテスト開始' }}
    </button>

    <p v-if="micCheckDone && micCheckOk" class="mic-check-ok-msg">✅ {{ micCheckMessage }}</p>

    <div v-if="micCheckDone && !micCheckOk" class="error-box">
      <p class="error-msg">⚠️ マイクがブロックされています</p>
      <p class="guide-txt">{{ micCheckMessage }}</p>
      <details class="ts-box">
        <summary>解決方法を確認する</summary>
        <div class="ts-content">
          <details>
            <summary>iPhoneの方</summary>
            <p>設定 ＞ Safari ＞ マイク を「許可」にしてください。</p>
            <div class="step2-images">
              <img v-if="iosMicImage1" :src="iosMicImage1" alt="iOS音声設定手順1" class="step2-img" />
              <img v-if="iosMicImage2" :src="iosMicImage2" alt="iOS音声設定手順2" class="step2-img" />
              <img v-if="iosMicImage3" :src="iosMicImage3" alt="iOS音声設定手順3" class="step2-img" />
            </div>
          </details>
          <details>
            <summary>Androidの方</summary>
            <details class="step2-detail" open>
              <summary>確認手順1．Chromeのマイク設定画面を確認</summary>
              <p>
                Chrome ＞ [︙] ＞ 設定 ＞ サイトの設定 ＞ マイク にアクセスしてください。
              </p>
              <video
                src="https://audio.otoeigo.com/manual/video2_lite.mp4"
                autoplay
                loop
                muted
                playsinline
                style="width: 100%; max-width: 360px; border-radius: 8px;"
              ></video>
              <details class="step2-detail">
                <summary>▼分からないときタップして開く</summary>
                <div class="step2-images">
                  <img v-if="androidMicDetailImg1" :src="androidMicDetailImg1" alt="Androidマイク設定 手順画像1" class="step2-img" />
                  <img v-if="androidMicDetailImg2" :src="androidMicDetailImg2" alt="Androidマイク設定 手順画像2" class="step2-img" />
                  <img v-if="androidMicDetailImg3" :src="androidMicDetailImg3" alt="Androidマイク設定 手順画像3" class="step2-img" />
                  <img v-if="androidMicDetailImg4" :src="androidMicDetailImg4" alt="Androidマイク設定 手順画像4" class="step2-img" />
                  <img v-if="androidMicDetailImg5" :src="androidMicDetailImg5" alt="Androidマイク設定 手順画像5" class="step2-img" />
                </div>
              </details>
              <p>以下のどちらですか？</p>
              <div class="device-choice-grid">
                <img
                  v-if="androidMicBranchDisabledImage"
                  :src="androidMicBranchDisabledImage"
                  alt="Androidマイク設定 グレーになって押せない例"
                  class="step2-img"
                />
                <img
                  v-if="androidMicBranchEnabledImage"
                  :src="androidMicBranchEnabledImage"
                  alt="Androidマイク設定 黒文字で選択できる例"
                  class="step2-img"
                />
              </div>
              <div class="device-choice-grid">
                <button
                  type="button"
                  class="btn-choice"
                  :class="{ active: androidMicBranchMode === 'disabled' }"
                  @click="androidMicBranchMode = 'disabled'"
                >
                  グレーになって押せない
                </button>
                <button
                  type="button"
                  class="btn-choice"
                  :class="{ active: androidMicBranchMode === 'enabled' }"
                  @click="androidMicBranchMode = 'enabled'"
                >
                  黒文字で選択できる
                </button>
              </div>
            </details>

            <details class="step2-detail" v-if="androidMicBranchMode === 'disabled'">
              <summary>手順2A．ファミリーリンク/フィルター設定を見直す</summary>
              <p>
                マイク設定がグレーで触れない場合は、Googleファミリーリンクや
                あんしんフィルター等で制限されています。そちらを先に解除してください。
              </p>
              <div class="step2-images">
                <img v-if="androidCookieImage1" :src="androidCookieImage1" alt="Androidファミリーリンク 手順1" class="step2-img" />
                <img v-if="androidCookieImage2" :src="androidCookieImage2" alt="Androidファミリーリンク 手順2" class="step2-img" />
                <img v-if="androidCookieImage3" :src="androidCookieImage3" alt="Androidファミリーリンク 手順3" class="step2-img" />
                <img v-if="androidCookieImage4" :src="androidCookieImage4" alt="Androidファミリーリンク 手順4" class="step2-img" />
              </div>
            </details>

            <details
              class="step2-detail"
              v-if="androidMicBranchMode === 'disabled' || androidMicBranchMode === 'enabled'"
            >
              <summary>手順2B．Chrome側でサイト許可設定</summary>
              <p>
                サイトがマイクへのアクセスを要求できる状態にしてください。<br />
                また「許可されていないサイト」の一覧にオトエイゴがあれば削除してください。
              </p>
              <video
                src="https://audio.otoeigo.com/manual/video3_lite.mp4"
                autoplay
                loop
                muted
                playsinline
                style="width: 100%; max-width: 360px; border-radius: 8px;"
              ></video>
            </details>

            <details
              class="step2-detail"
              v-if="androidMicBranchMode === 'disabled' || androidMicBranchMode === 'enabled'"
            >
              <summary>手順3B．端末設定でChromeマイク許可</summary>
              <p>
                設定 ＞ アプリ ＞ Chrome ＞ マイク ＞ <b>アプリの使用中のみ許可</b> にしてください。
              </p>
              <video
                src="https://audio.otoeigo.com/manual/video4_lite.mp4"
                autoplay
                loop
                muted
                playsinline
                style="width: 100%; max-width: 360px; border-radius: 8px;"
              ></video>
            </details>
          </details>
        </div>
      </details>
      <!-- トラブルシュート時に下へスクロールしても再テストできるよう、下部にも同じボタンを置く -->
      <button
        type="button"
        class="btn-action"
        @click="runMicCheck"
        :disabled="micCheckRunning"
      >
        {{ micCheckRunning ? 'テスト中...' : 'マイクテスト開始' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { supabase } from '../supabaseClient'

const emit = defineEmits(['success'])

const micCheckRunning = ref(false)
const micCheckDone = ref(false)
const micCheckOk = ref(false)
const micCheckMessage = ref('')
const androidMicBranchMode = ref('')

// iOS 音声設定手順画像（R2）
const iosMicImage1 = 'https://audio.otoeigo.com/manual/manual3.png'
const iosMicImage2 = 'https://audio.otoeigo.com/manual/manual4.png'
const iosMicImage3 = 'https://audio.otoeigo.com/manual/manual5.png'

// Android 音声設定手順画像（R2）
const androidMicBranchDisabledImage = 'https://audio.otoeigo.com/manual/gray.png'
const androidMicBranchEnabledImage = 'https://audio.otoeigo.com/manual/nongray.png'
const androidMicDetailImg1 = 'https://audio.otoeigo.com/manual/chromesetting1.png'
const androidMicDetailImg2 = 'https://audio.otoeigo.com/manual/chromesetting2.png'
const androidMicDetailImg3 = 'https://audio.otoeigo.com/manual/chromesetting3.png'
const androidMicDetailImg4 = 'https://audio.otoeigo.com/manual/chromesetting4.png'
const androidMicDetailImg5 = 'https://audio.otoeigo.com/manual/chromesetting5.png'
const androidCookieImage1 = 'https://audio.otoeigo.com/manual/family1.png'
const androidCookieImage2 = 'https://audio.otoeigo.com/manual/family2.png'
const androidCookieImage3 = 'https://audio.otoeigo.com/manual/family3.png'
const androidCookieImage4 = 'https://audio.otoeigo.com/manual/family4.png'

/** getUserMedia が解決せず UI が固まるのを防ぐ（Chrome 含む） */
const MIC_CHECK_TIMEOUT_MS = 20_000

const runMicCheck = async () => {
  if (micCheckRunning.value) return
  micCheckRunning.value = true
  micCheckDone.value = false
  micCheckOk.value = false
  micCheckMessage.value = ''
  let timeoutId = null
  const ac = typeof AbortController !== 'undefined' ? new AbortController() : null
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      micCheckOk.value = false
      micCheckMessage.value = 'このブラウザではマイク機能がサポートされていないか、利用できません。'
      return
    }

    if (navigator.permissions?.query) {
      try {
        const st = await navigator.permissions.query({ name: 'microphone' })
        if (st.state === 'denied') {
          micCheckOk.value = false
          micCheckMessage.value =
            'マイクへのアクセスがブロックされています。Chrome のアドレスバー横の鍵アイコン、または設定からマイクを許可してください。'
          return
        }
      } catch (_) {
        // microphone の Permissions API 未対応ブラウザは getUserMedia に任せる
      }
    }

    let stream
    if (ac) {
      timeoutId = setTimeout(() => ac.abort(), MIC_CHECK_TIMEOUT_MS)
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        signal: ac.signal
      })
    } else {
      const timeoutPromise = new Promise((_, rej) => {
        timeoutId = setTimeout(
          () => rej(Object.assign(new Error('mic check timeout'), { name: 'AbortError' })),
          MIC_CHECK_TIMEOUT_MS
        )
      })
      stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({ audio: true }),
        timeoutPromise
      ])
    }
    // 取得できたらすぐに停止
    stream.getTracks().forEach((t) => t.stop())
    micCheckOk.value = true
    micCheckMessage.value = 'この端末ではマイクが正常に利用できることを確認しました。'

    // 参考情報としてアカウント側にも記録しておく（ルーティングの判定には使わない）
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        await supabase
          .from('users_phase2')
          .update({ mic_check_completed: true, updated_at: new Date().toISOString() })
          .eq('id', user.id)
      }
    } catch (e) {
      console.error('mic_check_completed update failed', e)
    }

    emit('success')
  } catch (e) {
    console.error('mic check error', e)
    micCheckOk.value = false
    let msg = 'マイクの取得に失敗しました。ブラウザや端末の設定を確認してください。'
    if (e && e.name === 'NotAllowedError') {
      msg =
        'マイクへのアクセスがブロックされています。ブラウザや端末の設定からマイク許可を有効にしてください。'
    } else if (e && e.name === 'AbortError') {
      msg =
        'マイクの確認が時間内に完了しませんでした。Chrome のタブに表示されている許可ダイアログに答えるか、アドレスバー横のマイク／鍵アイコンから許可を確認し、もう一度お試しください。'
    }
    micCheckMessage.value = msg
  } finally {
    if (timeoutId != null) clearTimeout(timeoutId)
    micCheckRunning.value = false
    micCheckDone.value = true
  }
}
</script>

<style scoped>
.mic-check-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
}

.guide-txt {
  font-size: 0.95rem;
  color: #374151;
  line-height: 1.6;
  margin: 0;
}

.mic-check-running-hint {
  padding: 0.65rem 0.75rem;
  background: #eff6ff;
  border-radius: 8px;
  color: #1e3a8a;
}

.btn-action {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: #2563eb;
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
}

.btn-action:disabled {
  background: #93c5fd;
  cursor: not-allowed;
}

.mic-check-ok-msg {
  color: #15803d;
  font-weight: 700;
  margin: 0;
}

.error-box {
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.error-msg {
  color: #b91c1c;
  font-weight: 700;
  margin: 0;
}

.ts-box summary {
  cursor: pointer;
  font-weight: 600;
  color: #374151;
}

.ts-content {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.step2-detail {
  margin-top: 0.4rem;
}

.step2-images {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.4rem;
}

.step2-img {
  width: 100%;
  max-width: 360px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.device-choice-grid {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.4rem;
  flex-wrap: wrap;
}

.btn-choice {
  flex: 1;
  min-width: 140px;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #fff;
  cursor: pointer;
  font-size: 0.85rem;
}

.btn-choice.active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 700;
}
</style>
